import type { Microsoft365ImportTarget } from '@dbrain/shared';
import { Button, Icons } from '@dbrain/ui';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '../../../api/client.js';
import { useDashboard, useMe } from '../../../api/hooks.js';
import { useMicrosoft365Status } from '../../../api/microsoft365.js';
import { useSaveAttachment, useSavedAttachments } from '../../../api/microsoft365-files.js';
import { useMeta } from '../../../experience.js';
import { canAdd, canSaveAttachments, failureReason, importTargetOf, placeLabel, savedOf, targetId, targetOfPlace, targetsFor, type Target } from './model.js';
import { TargetChooser } from './TargetChooser.js';

const DEFAULT_MAX_MB = 100;

export interface AttachmentSave {
  /** the card has the button: Microsoft 365 and mail reading usable, in a conversation */
  offered: boolean;
  /** where it is saved (now or before, from the saved list after a reload) */
  place: Target | null;
  /** it was in that place already ("Zaten Belgeler'de") */
  duplicate: boolean;
  saving: boolean;
  /** why the last try did not save it, in words */
  failure: string | null;
  saveTo: (target: Microsoft365ImportTarget) => void;
}

/**
 * The state of "Belgelerime kaydet" for one attachment card (MsMailEkKaydet), shared by its button and its foot.
 * The attachment is named by its ref in this conversation only; the server finds the mail and the file again with
 * the person's own account. Saved before: the conversation's saved list says so after a reload.
 */
export function useAttachmentSave(conversationId: string | undefined, ref: string): AttachmentSave {
  const { t } = useTranslation();
  const status = useMicrosoft365Status(Boolean(conversationId)).data;
  const offered = Boolean(conversationId) && canSaveAttachments(status);
  const saved = useSavedAttachments(conversationId, offered);
  const save = useSaveAttachment(conversationId ?? '');
  const workspaces = useDashboard().data?.workspaces;
  const maxMb = useMeta().data?.uploadMaxMb ?? DEFAULT_MAX_MB;
  const done = save.data?.added[0];
  const earlier = savedOf(saved.data, ref);
  const place = done
    ? targetOfPlace(done.document.scope, done.document.workspaceId, workspaces ?? [])
    : earlier
      ? targetOfPlace(earlier.scope, earlier.workspaceId, workspaces ?? [])
      : null;
  const refusedItem = save.data?.failed[0];
  const failure = refusedItem
    ? failureReason(refusedItem.code, maxMb, t)
    : save.error
      ? t(`errors.${isApiError(save.error) ? save.error.code : 'internal_error'}.title`)
      : null;
  return {
    offered,
    place,
    duplicate: Boolean(done?.duplicate),
    saving: save.isPending,
    failure,
    saveTo: (target) => save.mutate({ ref, target }),
  };
}

/**
 * The card's button and its popover "Nereye kaydedilsin?": Kişisel (default), the person's teams and workspaces with
 * the warning in an attachment's words ("… bu maili almayanlar da.") and "Anladım", Herkes for a super admin (a
 * locked line for others); Vazgeç / Kaydet. Then "Kaydediliyor…", "Kaydedildi · Kişisel", "Zaten Belgeler'de" or
 * "Kaydedilemedi: …" (the states after the click have no mockup). The popover is a small dialog: Escape or a click
 * outside closes it and the focus goes back to the button.
 */
export function SaveAttachment({ save, sender }: { save: AttachmentSave; sender: string }) {
  const { t, i18n } = useTranslation();
  const me = useMe().data;
  const workspaces = useDashboard().data?.workspaces;
  const legendId = useId();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState('user');
  const [acknowledged, setAcknowledged] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const targets = useMemo(() => targetsFor({ role: me?.tenant.role, workspaces: workspaces ?? [], conversation: false }, i18n.language), [me?.tenant.role, workspaces, i18n.language]);
  const chosen = targets.options.find((option) => targetId(option) === target) ?? { kind: 'user' as const };

  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      button.current?.focus();
    };
    const onDown = (event: PointerEvent) => {
      const node = event.target instanceof Node ? event.target : null;
      if (node && !panel.current?.contains(node) && !button.current?.contains(node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  const submit = () => {
    const body = importTargetOf(chosen, acknowledged);
    if (!body) return;
    save.saveTo(body);
    setOpen(false);
    button.current?.focus();
  };

  if (save.place) {
    const place = placeLabel(save.place, t);
    return (
      <span className="m365-save__done" role="status" data-testid="m365-save-done" data-duplicate={save.duplicate ? 'true' : undefined}>
        <Icons.done size={15} />
        {save.duplicate ? t('microsoft365.files.save.already', { place }) : t('microsoft365.files.save.saved', { place })}
      </span>
    );
  }
  return (
    <span className="m365-save" data-testid="m365-save">
      <Button
        ref={button}
        variant="secondary"
        size="sm"
        className={open ? 'm365-save__button m365-save__button--open' : 'm365-save__button'}
        icon={<Icons.download size={15} />}
        aria-expanded={open}
        aria-haspopup="dialog"
        busy={save.saving}
        onClick={() => setOpen(!open)}
        data-testid="m365-save-button"
      >
        {save.saving ? t('microsoft365.files.save.saving') : t('microsoft365.files.save.button')}
      </Button>
      {save.failure && !save.saving ? (
        <span className="m365-save__failed" role="alert" data-testid="m365-save-failed">
          {t('microsoft365.files.save.failed', { reason: save.failure })}
        </span>
      ) : null}
      {open ? (
        <div ref={panel} className="m365-save__popover" role="dialog" aria-labelledby={legendId} data-testid="m365-save-popover">
          <TargetChooser
            save
            legendId={legendId}
            targets={targets}
            value={targetId(chosen)}
            onChange={(id) => {
              setTarget(id);
              setAcknowledged(false);
            }}
            acknowledged={acknowledged}
            onAcknowledge={setAcknowledged}
            wording="outlook"
            testId="m365-save-targets"
          />
          <p className="m365-save__note">
            {t('microsoft365.files.save.noteLead')}{' '}
            <strong>{sender ? t('microsoft365.files.save.noteLabel', { sender }) : t('microsoft365.files.save.noteLabelNoSender')}</strong>
            {t('microsoft365.files.save.noteTail')}
          </p>
          <div className="m365-save__actions">
            <Button variant="secondary" size="sm" onClick={close} data-testid="m365-save-cancel">
              {t('common.cancel')}
            </Button>
            <Button size="sm" disabled={!canAdd(1, chosen, acknowledged)} onClick={submit} data-testid="m365-save-submit">
              {t('microsoft365.files.save.submit')}
            </Button>
          </div>
        </div>
      ) : null}
    </span>
  );
}

/** The card's foot after saving (no mockup): "Ek belgelerinize kaydedildi · Kişisel". */
export function SavedNote({ place }: { place: Target }) {
  const { t } = useTranslation();
  return (
    <span className="att-card__note att-card__note--saved" data-testid="m365-save-foot">
      <Icons.done size={14} />
      {t('microsoft365.files.save.foot', { place: placeLabel(place, t) })}
    </span>
  );
}
